/**
 * @file src/_modulos/financeiro/listagem/columns/components/utils.tsx
 * @description Interfaces e componentes base reutilizáveis
 */

"use client";

import { Badge, HoverCard, HoverCardContent, HoverCardTrigger } from "visuais";
import { ReactNode } from "react";

/**
 * Configuração de badge
 */
export interface BadgePreset {
  color: string;
  icon: React.ReactNode;
  tooltip: string;
}

/**
 * Configuração de categoria para badges
 */
export interface CategoryConfig {
  color: string;
  icon: React.ComponentType<{ className?: string }>;
  getTooltip: () => string;
}

/**
 * Tamanhos de fonte responsivos padronizados
 */
export const RESPONSIVE_TEXT_SIZES =
  "text-[8pt] fhd:text-[9pt] qhd:text-[10pt] uhd:text-[11pt]";

/**
 * Tamanhos de fonte para células de tabela
 */
export const CELL_TEXT_SIZES =
  "text-[9pt] fhd:text-[10pt] qhd:text-[11pt] uhd:text-[12pt]";

/**
 * Gera cor de hover padronizada
 */
export function getHoverColor(baseColor: string): string {
  const colorMap: Record<string, string> = {
    "text-rose-600": "hover:border-rose-600/50 hover:dark:border-rose-400/50",
    "text-lime-600": "hover:border-lime-600/50 hover:dark:border-lime-400/50",
    "text-amber-600":
      "hover:border-amber-600/50 hover:dark:border-amber-400/50",
    "text-teal-600": "hover:border-teal-600/50 hover:dark:border-teal-400/50",
    "text-indigo-600":
      "hover:border-indigo-600/50 hover:dark:border-indigo-400/50",
    "text-destructive": "hover:border-destructive/50",
  };

  return colorMap[baseColor] || "";
}

/**
 * Cria preset de badge com estrutura padronizada
 */
export function createBadgePreset(
  config: CategoryConfig,
  label: string,
  color: string
): BadgePreset {
  const IconComponent = config.icon;

  return {
    color: `${color} ${getHoverColor(color)}`,
    icon: (
      <div className="flex items-center gap-1">
        <IconComponent className={`size-4 ${color}`} />
        <span className={`${RESPONSIVE_TEXT_SIZES} ${color}`}>{label}</span>
      </div>
    ),
    tooltip: config.getTooltip(),
  };
}

/**
 * Cria preset de badge apenas com ícone (sem texto duplicado)
 */
export function createIconOnlyPreset(
  config: CategoryConfig,
  color: string
): BadgePreset {
  const IconComponent = config.icon;

  return {
    color: `${color} ${getHoverColor(color)}`,
    icon: <IconComponent className={`size-4 ${color}`} />,
    tooltip: config.getTooltip(),
  };
}

/**
 * Cria preset para valores vazios
 */
export function createEmptyPreset(
  icon: ReactNode,
  tooltip: string
): BadgePreset {
  return {
    color: "text-muted-foreground",
    icon,
    tooltip,
  };
}

/**
 * Cria preset para valores inválidos
 */
export function createInvalidPreset(
  value: string,
  validValues: string[],
  icon: ReactNode
): BadgePreset {
  return {
    color: "text-destructive",
    icon: <span className="text-destructive">{icon}</span>,
    tooltip: `Valor inválido: "${value}". Valores aceitos: ${validValues.join(
      ", "
    )}.`,
  };
}

/**
 * Componente de badge com tooltip
 */
export function BadgeWithTooltip({
  preset,
  text,
}: {
  preset: BadgePreset;
  text?: string;
}) {
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Badge
          variant="outline"
          className={`text-muted-foreground cursor-help py-1 border ${preset.color} transition-all duration-300`}
        >
          <span>{preset.icon}</span>
          {text && (
            <span className={`${RESPONSIVE_TEXT_SIZES} ${preset.color}`}>
              {text}
            </span>
          )}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent className={`${RESPONSIVE_TEXT_SIZES} font-medium`}>
        {preset.tooltip}
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * Classes CSS padronizadas para células
 */
export const CELL_CLASSES = {
  centered: "flex justify-center",
  column: "flex flex-col items-center",
  columnText: "flex flex-col items-center text-center",
  responsiveText: CELL_TEXT_SIZES,
  smallText: RESPONSIVE_TEXT_SIZES,
} as const;
